import { useState } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { useForm, Controller, type Control, type UseFormRegister, type UseFormWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion } from 'framer-motion'
import { CheckCircle2, Pencil } from 'lucide-react'
import { toast } from 'sonner'
import { TAG_SUGGESTIONS, categoryLabel } from 'shared'
import StepShell from '@/components/form/StepShell'
import CategorySelect from '@/components/form/CategorySelect'
import TagInput from '@/components/form/TagInput'
import CurrencyInput from '@/components/form/CurrencyInput'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useSession } from '@/lib/auth-client'
import { useProviderProfile } from '@/hooks/useProviderProfile'
import { useCreateService } from '@/hooks/useProviderMutations'

const schema = z.object({
  name: z.string().trim().min(2, 'Name must be at least 2 characters'),
  category: z.string().min(1, 'Pick a category'),
  description: z.string().max(1000).optional().or(z.literal('')),
  tags: z.array(z.string()),
  priceFromCents: z.number().min(1, 'Set a price'),
  locationMode: z.enum(['profile', 'custom']),
  locationCity: z.string().optional().or(z.literal('')),
  locationRegion: z.string().optional().or(z.literal('')),
})

type ServiceForm = z.infer<typeof schema>

const STEPS = ['name', 'category', 'description', 'tags', 'price', 'location', 'review'] as const
const STEP_FIELDS: Record<(typeof STEPS)[number], (keyof ServiceForm)[]> = {
  name: ['name'],
  category: ['category'],
  description: ['description'],
  tags: ['tags'],
  price: ['priceFromCents'],
  location: [],
  review: [],
}

const inputClass = (hasError?: boolean) =>
  cn(
    'w-full px-4 py-3 rounded-xl border bg-card text-foreground text-lg placeholder:text-muted-foreground outline-none transition-colors focus-visible:ring-3 focus-visible:ring-ring/50',
    hasError ? 'border-destructive focus-visible:border-destructive' : 'border-input focus-visible:border-ring'
  )

export default function ServiceOnboarding() {
  const navigate = useNavigate()
  const { data: session, isPending: sessionPending } = useSession()
  const { data: profileData, isLoading: profileLoading } = useProviderProfile()
  const createService = useCreateService()

  const [step, setStep] = useState(0)
  const [direction, setDirection] = useState(1)
  const [createdId, setCreatedId] = useState<string | null>(null)

  const {
    register,
    control,
    watch,
    trigger,
    setError,
    getValues,
    reset,
    formState: { errors },
  } = useForm<ServiceForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      category: '',
      description: '',
      tags: [],
      priceFromCents: 0,
      locationMode: 'profile',
      locationCity: '',
      locationRegion: '',
    },
    mode: 'onTouched',
  })

  if (sessionPending || profileLoading) return <div className="min-h-screen bg-background" />
  if (!session) return <Navigate to="/login" replace />
  if (!profileData?.provider) return <Navigate to="/provider/onboarding" replace />

  const profileCity = profileData.provider.locations[0]?.city ?? 'my area'
  const submitted = createdId !== null

  const goTo = (next: number) => {
    setDirection(next > step ? 1 : -1)
    setStep(next)
  }

  const handleContinue = async () => {
    const name = STEPS[step]
    const fields = STEP_FIELDS[name]
    if (fields.length > 0 && !(await trigger(fields))) return

    if (name === 'location') {
      const { locationMode, locationCity, locationRegion } = getValues()
      if (locationMode === 'custom') {
        let ok = true
        if (!locationCity?.trim()) {
          setError('locationCity', { message: 'City is required' })
          ok = false
        }
        if (!locationRegion?.trim()) {
          setError('locationRegion', { message: 'Province is required' })
          ok = false
        }
        if (!ok) return
      }
    }

    if (step < STEPS.length - 1) return goTo(step + 1)

    // review step → submit
    const data = getValues()
    const location =
      data.locationMode === 'custom' && data.locationCity?.trim() && data.locationRegion?.trim()
        ? { city: data.locationCity.trim(), region: data.locationRegion.trim() }
        : undefined
    try {
      const res = await createService.mutateAsync({
        name: data.name.trim(),
        category: data.category,
        description: data.description?.trim() || undefined,
        tags: data.tags,
        priceFromCents: data.priceFromCents,
        location,
      })
      setCreatedId(res.service.id)
    } catch {
      toast.error("Couldn't create your service. Please try again.")
    }
  }

  const handleAddAnother = () => {
    reset()
    setCreatedId(null)
    setDirection(-1)
    setStep(0)
  }

  const values = getValues()
  const name = STEPS[step]

  return (
    <StepShell
      currentStep={submitted ? STEPS.length : step}
      totalSteps={submitted ? STEPS.length + 1 : STEPS.length}
      stepKey={submitted ? 'done' : name}
      direction={direction}
      onBack={step > 0 && !submitted ? () => goTo(step - 1) : undefined}
      onContinue={!submitted ? handleContinue : undefined}
      continueLabel={
        createService.isPending ? 'Creating…' : name === 'review' ? 'Create service' : 'Continue'
      }
      continueDisabled={createService.isPending}
      hideControls={submitted}
    >
      {submitted ? (
        <DoneStep serviceId={createdId} navigate={navigate} onAddAnother={handleAddAnother} />
      ) : (
        <>
          {name === 'name' && <NameStep register={register} error={errors.name?.message} />}
          {name === 'category' && <CategoryStep control={control} error={errors.category?.message} />}
          {name === 'description' && <DescriptionStep register={register} />}
          {name === 'tags' && <TagsStep control={control} category={values.category} />}
          {name === 'price' && <PriceStep control={control} error={errors.priceFromCents?.message} />}
          {name === 'location' && (
            <LocationStep register={register} control={control} watch={watch} errors={errors} profileCity={profileCity} />
          )}
          {name === 'review' && <ReviewStep values={values} profileCity={profileCity} onEdit={goTo} />}
        </>
      )}
    </StepShell>
  )
}

function NameStep({ register, error }: { register: UseFormRegister<ServiceForm>; error?: string }) {
  return (
    <div>
      <h1 className="text-3xl sm:text-4xl font-bold mb-3">What's your service called?</h1>
      <p className="text-muted-foreground mb-6">A clear name helps customers find you.</p>
      <input {...register('name')} placeholder="e.g. Downtown Movers Co." className={inputClass(!!error)} />
      {error && <p className="text-destructive text-sm mt-1.5">{error}</p>}
    </div>
  )
}

function CategoryStep({ control, error }: { control: Control<ServiceForm>; error?: string }) {
  return (
    <div>
      <h1 className="text-3xl sm:text-4xl font-bold mb-3">What type of service is this?</h1>
      <p className="text-muted-foreground mb-6">Pick the category that best describes what you offer.</p>
      <Controller
        name="category"
        control={control}
        render={({ field }) => <CategorySelect value={field.value} onChange={field.onChange} />}
      />
      {error && <p className="text-destructive text-sm mt-1.5">{error}</p>}
    </div>
  )
}

function DescriptionStep({ register }: { register: UseFormRegister<ServiceForm> }) {
  return (
    <div>
      <h1 className="text-3xl sm:text-4xl font-bold mb-3">Describe your service</h1>
      <p className="text-muted-foreground mb-6">What should customers know? What makes you stand out?</p>
      <textarea
        {...register('description')}
        rows={5}
        maxLength={1000}
        placeholder="We provide professional, fully-insured moving services across the city…"
        className={cn(inputClass(), 'resize-none')}
      />
    </div>
  )
}

function TagsStep({ control, category }: { control: Control<ServiceForm>; category: string }) {
  const suggestions = TAG_SUGGESTIONS[category] ?? []
  return (
    <div>
      <h1 className="text-3xl sm:text-4xl font-bold mb-3">Add some tags</h1>
      <p className="text-muted-foreground mb-6">Tags make your service easier to discover.</p>
      <Controller
        name="tags"
        control={control}
        render={({ field }) => (
          <TagInput tags={field.value} onChange={field.onChange} suggestions={suggestions} />
        )}
      />
    </div>
  )
}

function PriceStep({ control, error }: { control: Control<ServiceForm>; error?: string }) {
  return (
    <div>
      <h1 className="text-3xl sm:text-4xl font-bold mb-3">Set your starting price</h1>
      <p className="text-muted-foreground mb-6">
        The lowest price a customer can expect. You can adjust this later.
      </p>
      <Controller
        name="priceFromCents"
        control={control}
        render={({ field }) => <CurrencyInput value={field.value} onChange={field.onChange} />}
      />
      {error && <p className="text-destructive text-sm mt-1.5">{error}</p>}
    </div>
  )
}

function LocationStep({
  register,
  control,
  watch,
  errors,
  profileCity,
}: {
  register: UseFormRegister<ServiceForm>
  control: Control<ServiceForm>
  watch: UseFormWatch<ServiceForm>
  errors: ReturnType<typeof useForm<ServiceForm>>['formState']['errors']
  profileCity: string
}) {
  const mode = watch('locationMode')
  const optionClass = (selected: boolean) =>
    cn(
      'w-full text-left px-4 py-3 rounded-xl border text-sm font-medium transition-colors',
      selected
        ? 'border-primary bg-primary/10 text-foreground'
        : 'border-border bg-card text-foreground hover:border-foreground/30'
    )

  return (
    <div>
      <h1 className="text-3xl sm:text-4xl font-bold mb-3">Where is this service offered?</h1>
      <p className="text-muted-foreground mb-6">
        Leave it on your profile location, or set one just for this service.
      </p>
      <Controller
        name="locationMode"
        control={control}
        render={({ field }) => (
          <div className="space-y-2">
            <button type="button" onClick={() => field.onChange('profile')} className={optionClass(field.value === 'profile')}>
              Same as my profile ({profileCity})
            </button>
            <button type="button" onClick={() => field.onChange('custom')} className={optionClass(field.value === 'custom')}>
              A different location
            </button>
          </div>
        )}
      />
      {mode === 'custom' && (
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <input {...register('locationCity')} placeholder="City" className={cn(inputClass(!!errors.locationCity), 'text-base')} />
            {errors.locationCity && <p className="text-destructive text-sm mt-1">{errors.locationCity.message}</p>}
          </div>
          <div>
            <input {...register('locationRegion')} placeholder="Province" className={cn(inputClass(!!errors.locationRegion), 'text-base')} />
            {errors.locationRegion && <p className="text-destructive text-sm mt-1">{errors.locationRegion.message}</p>}
          </div>
        </div>
      )}
    </div>
  )
}

function ReviewStep({
  values,
  profileCity,
  onEdit,
}: {
  values: ServiceForm
  profileCity: string
  onEdit: (step: number) => void
}) {
  const priceDisplay = values.priceFromCents
    ? `From $${(values.priceFromCents / 100).toFixed(2).replace(/\.00$/, '')}`
    : 'Not set'
  const locationDisplay =
    values.locationMode === 'custom' && values.locationCity
      ? `${values.locationCity}${values.locationRegion ? `, ${values.locationRegion}` : ''}`
      : `Same as profile (${profileCity})`

  return (
    <div>
      <h1 className="text-3xl sm:text-4xl font-bold mb-3">Looking good!</h1>
      <p className="text-muted-foreground mb-6">Review your service before publishing.</p>
      <div className="space-y-3">
        <ReviewRow label="Name" value={values.name} onEdit={() => onEdit(0)} />
        <ReviewRow label="Category" value={categoryLabel(values.category)} onEdit={() => onEdit(1)} />
        <ReviewRow label="Description" value={values.description || 'None'} onEdit={() => onEdit(2)} />
        <ReviewRow label="Tags" value={values.tags.length > 0 ? values.tags.join(', ') : 'None'} onEdit={() => onEdit(3)} />
        <ReviewRow label="Starting price" value={priceDisplay} onEdit={() => onEdit(4)} />
        <ReviewRow label="Location" value={locationDisplay} onEdit={() => onEdit(5)} />
      </div>
    </div>
  )
}

function ReviewRow({ label, value, onEdit }: { label: string; value: string; onEdit: () => void }) {
  return (
    <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-card border border-border">
      <div className="min-w-0">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="font-medium truncate">{value}</p>
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="text-primary hover:text-primary/80 transition-colors shrink-0"
      >
        <Pencil className="size-5" />
      </button>
    </div>
  )
}

function DoneStep({
  serviceId,
  navigate,
  onAddAnother,
}: {
  serviceId: string
  navigate: (path: string) => void
  onAddAnother: () => void
}) {
  return (
    <div className="text-center">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.1 }}
        className="w-16 h-16 rounded-full bg-primary/15 text-primary flex items-center justify-center mx-auto mb-6"
      >
        <CheckCircle2 className="size-10" />
      </motion.div>
      <h1 className="text-3xl sm:text-4xl font-bold mb-3">Service created!</h1>
      <p className="text-lg text-muted-foreground mb-8 max-w-md mx-auto">
        Your service is now live. Customers can find and book it.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button size="lg" onClick={() => navigate(`/services/${serviceId}`)}>
          View your service
        </Button>
        <Button size="lg" variant="outline" onClick={() => navigate('/provider')}>
          Go to dashboard
        </Button>
        <Button size="lg" variant="ghost" onClick={onAddAnother}>
          Add another
        </Button>
      </div>
    </div>
  )
}
